import isEmpty from 'lodash/isEmpty'
import {
  PRODUCT_GRID_TYPE,
  EMPTY_GRID_TYPE,
  AD_SHOWCASE_GRID_TYPE,
  SHELF_GRID_TYPE,
  ENDCAP_GRID_TYPE,
  COMPLEMENTARY_SHELF_GRID_TYPE,
} from '@/features/shared/constants/productGridTypes'
import { type Grid, type GenerateGridType } from '@/features/shop/components/ProductGrid/types'
import { placeEndcapOnGrid } from '@/features/shop/components/ProductGrid/utils/endcaps'
import { getBuyAgainShelf } from '@/features/shop/services/Search/utils'

export const generateGrid = ({
  gridColumns,
  searchResults,
  adShowcase,
  showBuyAgainShelf,
  isSearchGrid,
  complementaryShelf,
  complementaryShelfProductIndex,
}: GenerateGridType): Grid => {
  const { products = [], endcapsBySlotPos, shelves } = searchResults
  // We start the grid with just the products
  let grid: Grid = products.map((product): Grid[number] => ({
    renderType: PRODUCT_GRID_TYPE,
    data: product,
  }))

  // on the initial render gridColumns is 0 (not yet measured).
  // return products only so the renderer can calculate gridColumns.
  // all layout dependent insertions (endcaps, showcases, banners, shelves),
  // are deferred until gridColumns is known.
  if (!gridColumns) return grid

  // endcaps (share rows with products, placed first)

  if (endcapsBySlotPos && !isEmpty(endcapsBySlotPos)) {
    for (let index = 0; index < grid.length; index++) {
      const card = grid[index]
      if (card?.renderType === PRODUCT_GRID_TYPE) {
        grid = placeEndcapOnGrid(grid, gridColumns, index, endcapsBySlotPos)
      }
    }
  }

  // full width rows (fixed row_position items)
  if (adShowcase?.data?.items && adShowcase.data.items.length > 0) {
    let rowPosition = adShowcase.metadata.read_operation_info?.row_position ?? 0
    if (!rowPosition) {
      if (adShowcase.content_type_id === 'ad_showcase') rowPosition = 2
      if (adShowcase.content_type_id === 'ad_video_showcase') rowPosition = 3
    }

    grid = insertAfterVisualRow({
      grid,
      gridColumns,
      rowPosition,
      item: {
        renderType: AD_SHOWCASE_GRID_TYPE,
        data: adShowcase,
      },
    })
  }

  if (showBuyAgainShelf && isSearchGrid) {
    const buyAgainShelf = getBuyAgainShelf(shelves)

    if (buyAgainShelf) {
      const rowPosition = buyAgainShelf.row_position ?? 0

      if (rowPosition === 0) {
        grid.splice(0, 0, {
          renderType: SHELF_GRID_TYPE,
          data: buyAgainShelf,
        })
      } else {
        grid = insertAfterVisualRow({
          grid,
          gridColumns,
          rowPosition,
          item: {
            renderType: SHELF_GRID_TYPE,
            data: buyAgainShelf,
          },
        })
      }
    }
  }

  // relative placements (must run after fixed row_position items)
  if (complementaryShelf && complementaryShelfProductIndex !== undefined && isSearchGrid) {
    const clickedIndex = findNthProductIndex(grid, complementaryShelfProductIndex)
    if (clickedIndex !== -1) {
      // count visual slots before the clicked product to determine its column
      const slotsBefore = totalSlotsBefore(grid, clickedIndex)
      const columnInRow = slotsBefore % gridColumns
      const remainingSlots = gridColumns - 1 - columnInRow

      // scan forward counting visual slots to find end of this row
      const insertAt = findGridIndexAfterSlots({
        grid,
        startIndex: clickedIndex,
        targetSlots: remainingSlots,
      })

      grid.splice(insertAt + 1, 0, {
        renderType: COMPLEMENTARY_SHELF_GRID_TYPE,
        data: complementaryShelf,
      })
    }
  }

  // pad the last row with empty cards to prevent orphaned products
  // from stretching to fill the remaining width of the grid.
  const totalVisualSlots = totalSlotsBefore(grid, grid.length)
  grid.push(...createRowPadding({ usedSlots: totalVisualSlots, gridColumns }))

  return grid
}

// returns the number of visual column slots a grid item consumes.
// products take 1 slot, endcaps take their `slot_size`, and full width
// items (showcases, banners, shelves) don't consume row slots.
const getSlotSize = (card: Grid[number]): number => {
  if (card.renderType === PRODUCT_GRID_TYPE) return 1
  if (card.renderType === ENDCAP_GRID_TYPE) return card.data?.slot_size ?? 1
  // full width items and empty cards don't consume row slots
  return 0
}

// counts the total visual column slots consumed by items before
// a given grid array index. used to determine which column an item sits in
const totalSlotsBefore = (grid: Grid, endIndex: number): number => {
  let slots = 0
  for (let i = 0; i < endIndex; i++) {
    const card = grid[i]
    if (card) slots += getSlotSize(card)
  }
  return slots
}

// finds the grid array index of the Nth product card,
// skipping over any previously inserted non product items.
const findNthProductIndex = (grid: Grid, n: number): number => {
  let count = 0
  for (let i = 0; i < grid.length; i++) {
    if (grid[i]?.renderType === PRODUCT_GRID_TYPE) {
      if (count === n) return i
      count++
    }
  }
  return -1
}

// scans forward from startIndex, counting visual slots until
// targetSlots are filled. returns the grid index of the last
// item that fits.
//
// used for both:
// - finding where a visual row ends (start from 0, target = row * columns)
// - finding end of a partially filled row (start from clicked product,
//   target = remaining slots in that row
const findGridIndexAfterSlots = ({
  grid,
  startIndex,
  targetSlots,
}: {
  grid: Grid
  startIndex: number
  targetSlots: number
}): number => {
  let lastIndex = startIndex
  let filled = 0

  for (let i = startIndex + 1; i < grid.length && filled < targetSlots; i++) {
    const card = grid[i]
    if (!card) continue

    const size = getSlotSize(card)
    if (size === 0) continue
    if (filled + size > targetSlots) break

    filled += size
    lastIndex = i
  }

  return lastIndex
}

// finds the last grid item that completes a given visual row,
// accounting for endcaps consuming multiple column slots.
const findEndOfVisualRow = ({
  grid,
  gridColumns,
  rowPosition,
}: {
  grid: Grid
  gridColumns: number
  rowPosition: number
}): number => {
  const targetSlots = rowPosition * gridColumns
  let consumed = 0
  let lastIndex = -1

  for (let i = 0; i < grid.length; i++) {
    const card = grid[i]
    if (!card) continue

    const size = getSlotSize(card)
    if (size === 0) continue

    consumed += size
    lastIndex = i

    if (consumed >= targetSlots) break
  }

  return lastIndex
}

// creates empty grid cards to pad an incomplete row.
// usedSlots is the total visual slots consumed up to the insertion point.
const createRowPadding = ({
  usedSlots,
  gridColumns,
}: {
  usedSlots: number
  gridColumns: number
}): Grid => {
  const remainder = usedSlots % gridColumns
  const count = remainder === 0 ? 0 : gridColumns - remainder

  return Array.from({ length: count }, () => ({
    renderType: EMPTY_GRID_TYPE,
    data: {},
  }))
}

// splices a single full width item into the grid after a given visual row,
// padding any incomplete row with empty cards first.
const insertAfterVisualRow = ({
  grid,
  gridColumns,
  rowPosition,
  item,
}: {
  grid: Grid
  gridColumns: number
  rowPosition: number
  item: Grid[number]
}): Grid => {
  let insertAt = findEndOfVisualRow({ grid, gridColumns, rowPosition })

  // not enough products to fill this row — fall back to after the last product
  if (insertAt === -1) {
    for (let i = grid.length - 1; i >= 0; i--) {
      if (grid[i]?.renderType === PRODUCT_GRID_TYPE) {
        insertAt = i
        break
      }
    }
  }

  if (insertAt === -1) return grid

  const usedSlots = totalSlotsBefore(grid, insertAt + 1)
  const padding = createRowPadding({ usedSlots, gridColumns })

  grid.splice(insertAt + 1, 0, ...padding, item)
  return grid
}
